/**
 * Draw order, declared once.
 *
 * MapLibre had a flat list of layer ids and an insertion point computed from it. Leaflet has
 * panes: containers with a `z-index`, created on the map and named on every layer that goes
 * into one. The invariant is the same and it is the reason this list exists in one place
 * rather than as a `pane:` argument scattered over six call sites — the dated imagery must
 * sit UNDER the contours, or switching it on buries the very outlines it is there to be
 * compared against.
 *
 * Leaflet's own panes are kept where they already do the right thing: `overlayPane` (400)
 * holds whatever Geoman is drawing, `markerPane` (600) holds the issue and search marks
 * together with Geoman's vertex handles.
 */

import type * as L from "leaflet";

export const PANES = {
  /** The always-on photograph. */
  basemap: "k-basemap",
  /** The dated Earth Engine imagery, over the basemap and under everything of ours. */
  preview: "k-preview",
  /** Dark halo under the field outline: a bone hairline over sunlit stubble is 1.53:1. */
  fieldsCasing: "k-fields-casing",
  fields: "k-fields",
  /** Field names: above the contours, below Geoman's vertex handles in `markerPane`. */
  labels: "k-labels",
  /**
   * Hand-placed markers on the selected field. Above the names, because a flag is a thing
   * you click and a name is not — and BELOW `markerPane`, because that is where Geoman keeps
   * its vertex handles: a flag over a handle would swallow the click that drags a vertex.
   */
  notes: "k-notes",
} as const;

export type PaneName = (typeof PANES)[keyof typeof PANES];

/** Bottom to top. Leaflet's built-ins are listed so the order can be asserted as a whole. */
export const PANE_ORDER: { name: string; zIndex: number; ours: boolean }[] = [
  { name: PANES.basemap, zIndex: 200, ours: true },
  { name: PANES.preview, zIndex: 250, ours: true },
  { name: PANES.fieldsCasing, zIndex: 350, ours: true },
  { name: PANES.fields, zIndex: 360, ours: true },
  { name: "overlayPane", zIndex: 400, ours: false },
  { name: PANES.labels, zIndex: 450, ours: true },
  { name: PANES.notes, zIndex: 460, ours: true },
  { name: "markerPane", zIndex: 600, ours: false },
];

/**
 * Creates our panes on a fresh map. Leaflet's own are already there.
 *
 * The pane name is also put on the element as a class. Leaflet's own class for a custom pane
 * is `leaflet-<name>-pane`, which reads as though the name were Leaflet's — and it is ours,
 * used by CSS and by the tests to find the pane at all.
 */
export const createPanes = (map: L.Map): void => {
  for (const pane of PANE_ORDER) {
    if (!pane.ours) continue;
    const created = map.createPane(pane.name);
    created.classList.add(pane.name);
    created.style.zIndex = String(pane.zIndex);
  }
};
